import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useAuth from '@contexts/useAuth';
import { usePageTitle } from '@contexts/usePageTitle';
import SettingsTabs from '@components/SettingsTabs/SettingsTabs';
import { SETTING_TABS } from '@constants/settingTabs';
import { FeatureGated, FeatureGate } from '@featureFlags/index';
import { LoadingSpinner } from '@components/LoadingSpinner';
import SecuritySettings from './SecuritySettings/SecuritySettings';
import AccountSettings from './AccountSettings/AccountSettings';
import PreferencesSettings from './PreferencesSettings/PreferencesSettings';
import BillingSettings from './BillingSettings/BillingSettings';
import Organization from './Organization/Organization';
import LocalRunners from './LocalRunners/LocalRunners';
import OrgTools from './OrgTools/OrgTools';
import MemoriesSettings from './MemoriesSettings/MemoriesSettings';
import NotificationsSettings from './NotificationsSettings/NotificationsSettings';
import { Wrapper, ContentWrapper, ContentInner } from './Settings.styled';

const SettingsPage: React.FC = () => {
  const intl = useIntl();
  usePageTitle(
    intl.formatMessage({ id: 'settings.title', defaultMessage: 'Settings' }),
    intl.formatMessage({
      id: 'settings.help',
      defaultMessage: 'Your account, preferences, security, billing, organization and local runners.',
    }),
  );
  const { user } = useAuth();
  const location = useLocation();

  const isOrgAdmin =
    user?.orgRole === 'AIBUILD_ADMIN' || user?.orgRole === 'ORG_ADMIN';

  const activeTab = useMemo(() => {
    const tabs = Object.values(SETTING_TABS);
    const match = tabs.find((t) => location.pathname.startsWith(t.link));
    return match?.title ?? SETTING_TABS.ACCOUNT.title;
  }, [location.pathname]);

  return (
    <Wrapper data-testid="settings-page">
      <SettingsTabs activeTab={activeTab} />

      <ContentWrapper>
        <ContentInner>
          <Routes>
            <Route path="account" element={<AccountSettings />} />
            <Route path="preferences" element={<PreferencesSettings />} />
            <Route path="security" element={<SecuritySettings />} />
            {isOrgAdmin && (
              <Route path="billing" element={<BillingSettings />} />
            )}
            <Route
              path="tools"
              element={
                <FeatureGated
                  gate={FeatureGate.CUSTOM_TOOL_WORKBENCH}
                  fallback={<Navigate to="/settings/account" replace />}
                  pending={<LoadingSpinner />}
                >
                  {isOrgAdmin ? (
                    <OrgTools />
                  ) : (
                    <Navigate to="/settings/account" replace />
                  )}
                </FeatureGated>
              }
            />
            <Route path="organization" element={<Organization />} />
            <Route path="memories" element={<MemoriesSettings />} />
            <Route path="notifications" element={<NotificationsSettings />} />
            <Route path="local-runners" element={<LocalRunners />} />
            <Route path="*" element={<Navigate to="/settings/account" replace />} />
          </Routes>
        </ContentInner>
      </ContentWrapper>
    </Wrapper>
  );
};

export default SettingsPage;
